<script setup lang="ts">
defineProps({ blok: Object })
</script>

<template>
    <section class="py-20 overflow-hidden">
        <UiContainer max-width="max-w-3xl">
            <header class="insight-category-header">
                <img
                    :src="blok?.icon.filename"
                    :alt="blok?.icon.alt"
                >
                <StoryblokRichText :doc="blok?.header" />
            </header>
        </UiContainer>
        <UiContainer>
            <div class="grid grid-cols-2 gap-x-20 max-[641px]:grid-cols-1 max-[641px]:gap-x-0 max-[641px]:gap-y-10">
                <div class="insighr-category-description">
                    <StoryblokRichText :doc="blok?.description" />
                </div>
                <div class="">
                    <div class="relative -z-1">
                        <img
                            :src="blok?.map_countries.filename"
                            :alt="blok?.map_countries.alt"
                            class="max-w-none absolute top-0 left-0 max-[641px]:hidden"
                        >
                        <img
                            :src="blok?.map_north_africa.filename"
                            :alt="blok?.map_north_africa.alt"
                            class="max-w-none left-0 absolute top-0 -translate-y-full max-[641px]:hidden"
                        >
                    </div>
                    <div class="mt-[185px] [&>div>p]:text-center [&>div>p]:text-chsc-indigo-500 [&>div>p]:fs-3xl [&>div>p>strong]:fs-4xl [&>div>p>strong]:text-chsc-yellow [&>div>p]:font-londrina-solid max-[641px]:mt-0">
                        <div>
                            <StoryblokRichText :doc="blok?.forest_lost" />
                        </div>
                        <div class="mt-12">
                            <StoryblokRichText :doc="blok?.cocoa" />
                        </div>
                    </div>
                </div>
            </div>
        </UiContainer>
        <UiContainer max-width="max-w-3xl">
            <div class="mt-32 text-center [&>h4]:text-chsc-indigo-500 [&>h4]:fs-4xl [&>h4]:font-londrina-solid [&>h4]:mb-6 [&>p]:font-eb-garamond [&>p]:fs-2xl [&>p]:mb-6 [&>p:last-child]:mb-0">
                <StoryblokRichText :doc="blok?.addressing" />
            </div>
        </UiContainer>
        <UiContainer>
            <div
                v-motion-slide-visible-once-bottom
                class="relative mt-20 border-[2px] border-chsc-indigo-500 rounded-[40px] p-12"
            >
                <h4 class="absolute top-0 left-12 -translate-y-1/2 bg-white px-4 fs-4xl font-londrina-solid text-chsc-indigo-500 mb-6">
                    {{ blok?.response_title }}
                </h4>
                <div class="grid grid-cols-3 gap-x-6 [&>div>p]:font-londrina-solid [&>div>p]:fs-4xl [&>div>p]:text-chsc-indigo-500 [&>div>p]:text-center [&>div>p>strong]:fs-8xl [&>div>p>strong]:text-chsc-yellow [&>div>p>strong]:font-normal max-[641px]:grid-cols-1 max-[641px]:gap-x-0 max-[641px]:gap-y-6">
                    <div>
                        <StoryblokRichText :doc="blok?.response_1" />
                    </div>
                    <div>
                        <StoryblokRichText :doc="blok?.response_2" />
                    </div>
                    <div>
                        <StoryblokRichText :doc="blok?.response_3" />
                    </div>
                </div>
            </div>
        </UiContainer>
        <UiContainer>
            <div class="mt-20 grid grid-cols-2 gap-x-20 max-[641px]:grid-cols-1 max-[641px]:gap-x-0 max-[641px]:gap-y-10">
                <div class="flex flex-col gap-y-4 items-center justify-center">
                    <p class="text-sm font-bold text-center text-chsc-indigo-500">
                        {{ blok?.results_chart_title }}
                    </p>
                    <div>
                        <ClientOnly>
                            <apex-charts
                                v-if="blok"
                                type="pie"
                                width="100%"
                                height="300"
                                :options="
                                    {
                                        chart: {
                                            type: 'pie',
                                        },
                                        colors: ['#360086', '#FFC14A', '#9780BC'],
                                        stroke: {
                                            show: false,
                                        },
                                        legend: {
                                            show: false,
                                        },
                                        dataLabels: {
                                            enabled: true,
                                        },
                                        tooltip: {
                                            enabled: false,
                                        },
                                        responsive: [{
                                            breakpoint: 480,
                                            options: {
                                                chart: {
                                                    width: 200,
                                                },
                                                legend: {
                                                    position: 'bottom',
                                                },
                                            },
                                        }],
                                    }"
                                :series="[Number(blok?.results_chart_number), Number(100 - (Number(blok?.results_chart_number)))]"
                            />
                        </ClientOnly>
                    </div>
                    <div class="flex items-center justify-center gap-x-4">
                        <div class="flex items-center gap-x-2">
                            <div class="w-[20px] h-[20px] bg-chsc-indigo-500 rounded-full" />
                            <p class="text-xs font-bold">
                                {{ blok?.results_chart_legend_1 }}
                            </p>
                        </div>
                        <div class="flex items-center gap-x-2">
                            <div class="w-[20px] h-[20px] bg-chsc-yellow rounded-full" />
                            <p class="text-xs font-bold">
                                {{ blok?.results_chart_legend_2 }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="[&>h4]:text-chsc-indigo-500 [&>h4]:font-londrina-solid [&>h4]:fs-4xl [&>h4]:mb-6 [&>p]:font-eb-garamond [&>p]:fs-2xl [&>p]:mb-6 [&>p:last-child]:mb-0">
                    <StoryblokRichText :doc="blok?.results_description" />
                </div>
            </div>
        </UiContainer>
        <!-- <UiContainer>
            <div class="mt-20 bg-chsc-indigo-500 rounded-[40px] p-20 max-[641px]:p-12">
                <div class="flex gap-x-20">
                    <div class="max-[769px]:hidden">
                        <img
                            :src="blok?.eudr_icon.filename"
                            :alt="blok?.eudr_icon.alt"
                            class="max-w-none"
                        >
                    </div>
                    <div class="[&>h4]:text-chsc-yellow [&>h4]:font-londrina-solid [&>h4]:fs-4xl [&>h4]:mb-6 [&>p]:font-eb-garamond [&>p]:fs-2xl [&>p]:text-white [&>p]:mb-6 [&>p:last-child]:mb-0">
                        <StoryblokRichText :doc="blok?.eudr_description" />
                    </div>
                </div>
            </div>
        </UiContainer> -->
    </section>
</template>
